import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import {
  useApiAdminsegmentsIdPatch,
  getApiAdminsegmentsIdGetQueryKey
} from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Segment = SegmentSegmentCollectionReadModel;

interface UpdateSegmentFormProps {
  segment: Segment;
  onSuccess: () => void;
}

/** Formulaire de modification du nom et de la description d'un segment existant. */
export const UpdateSegmentForm: FC<UpdateSegmentFormProps> = ({ segment, onSuccess }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();

  const FormSchema = z.object({
    name: z
      .string()
      .min(1, t('form.error.required'))
      .max(255, t('form.error.char.max', { maxChar: 255 })),
    description: z
      .string()
      .max(1000, t('form.error.char.max', { maxChar: 1000 }))
      .optional()
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: segment.name ?? '',
      description: segment.description ?? ''
    }
  });

  const updateSegmentMutation = useApiAdminsegmentsIdPatch({
    mutation: {
      onSuccess: (updatedSegment) => {
        queryClient.setQueryData(
          getApiAdminsegmentsIdGetQueryKey(String(segment.id)),
          updatedSegment
        );
        toast.success(t('admin.segments.update.success'));
        onSuccess();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'patch', '/admin/segments')) {
          showOrvalErrorToast(error, t('admin.segments.update.error'));
        } else {
          toast.error(t('admin.segments.update.error'));
        }
      }
    }
  });

  function onSubmit(data: FormValues) {
    updateSegmentMutation.mutate({
      id: String(segment.id),
      data: {
        name: data.name,
        description: data.description || null
      }
    });
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.segments.form.name.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('admin.segments.form.name.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.segments.form.description.label')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('admin.segments.form.description.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          rounded="full"
          loading={updateSegmentMutation.isPending}
          className="w-full"
          type="submit"
        >
          {t('admin.segments.update.form.submit')}
        </Button>
      </form>
    </Form>
  );
};
